import { fromJS, Map as ImmutableMap } from 'immutable'
import { createSelector } from 'reselect'

import {
  EapHpiNotificationType,
  EapToHpiBannerStatus,
} from '@lyrahealth-inc/shared-app-logic/src/features/eligibility/types'
import { NotificationItemState } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import { getIsEapHpiInProductNotificationsEnabled } from '../featureFlags/featureFlagSelectors'
import {
  getEapHpiAccountBannerNotificationState,
  getEapHpiHomebaseBannerNotificationState,
} from '../user/userSelectors'

/**
 * Get the raw EAP HPI banner state from Redux
 */
const getEapHpiBannerState = (state: Map<string, any> | ImmutableMap<string, any>) => state?.get('eapHpiBanner')

/**
 * Get the loading state for EAP HPI banner
 */
export const getIsEapHpiBannerLoaded = createSelector(getEapHpiBannerState, (bannerState): boolean => {
  return bannerState?.get('isEapHpiBannerStateLoaded')
})

/**
 * Get the notification type from banner data
 */
export const getEapHpiBannerNotificationType = createSelector(
  getEapHpiBannerState,
  (bannerData): EapHpiNotificationType | null => {
    return bannerData?.get('notificationType') || null
  },
)

/**
 * Get the sessions remaining from banner data
 */
export const getEapHpiBannerSessionsRemaining = createSelector(getEapHpiBannerState, (bannerData): number => {
  return bannerData?.get('sessionsRemaining') ?? 0
})

/**
 * Get the provider Lyra ID from banner data
 */
export const getEapHpiBannerProviderLyraId = createSelector(getEapHpiBannerState, (bannerData): string | null => {
  return bannerData?.get('providerLyraId') || null
})

/**
 * Get the session type from banner data
 */
export const getEapHpiBannerSessionType = createSelector(getEapHpiBannerState, (bannerData): string | null => {
  return bannerData?.get('sessionType') || null
})

/**
 * Get the child Lyra ID from banner data (for dependent flows)
 */
export const getEapHpiBannerChildLyraId = createSelector(getEapHpiBannerState, (bannerData): string | undefined => {
  return bannerData?.get('childLyraId')
})

/**
 * Get the flow type (adult or dependent) from banner data
 */
export const getEapHpiBannerFlowAs = createSelector(getEapHpiBannerState, (bannerData): string | null => {
  return bannerData?.get('flowAs') || null
})

/**
 * Get the expiration date from banner data
 */
export const getEapHpiBannerExpirationDate = createSelector(getEapHpiBannerState, (bannerData): string | null => {
  return bannerData?.get('expirationDate') || null
})

/**
 * Check if the banner has expired
 */
export const getIsEapHpiBannerExpired = createSelector(getEapHpiBannerExpirationDate, (expirationDate): boolean => {
  if (!expirationDate) {
    return false
  }
  return new Date(expirationDate) < new Date()
})

const isVisible = (
  isFeatureEnabled: boolean,
  isLoaded: boolean,
  notificationState: NotificationItemState,
  bannerState: EapToHpiBannerStatus | null,
  isExpired: boolean,
): boolean => {
  if (!isFeatureEnabled) {
    return false
  }

  if (!isLoaded) {
    return false
  }

  if (!notificationState || notificationState === 'dismissed' || notificationState === 'completed') {
    return false
  }

  if (!bannerState) {
    return false
  }

  if (isExpired) {
    return false
  }

  return true
}

/**
 * Check if the EAP HPI banner should be visible
 * Combines feature flag, notification state, and expiration check
 */
export const getIsEapHpiHomebaseBannerVisible = createSelector(
  [
    getIsEapHpiInProductNotificationsEnabled,
    getIsEapHpiBannerLoaded,
    getEapHpiHomebaseBannerNotificationState,
    getEapHpiBannerState,
    getIsEapHpiBannerExpired,
  ],
  isVisible,
)

/**
 * Check if the EAP HPI banner should be visible
 * Combines feature flag, notification state, and expiration check
 */
export const getIsEapHpiAccountBannerVisible = createSelector(
  [
    getIsEapHpiInProductNotificationsEnabled,
    getIsEapHpiBannerLoaded,
    getEapHpiAccountBannerNotificationState,
    getEapHpiBannerState,
    getIsEapHpiBannerExpired,
  ],
  isVisible,
)

/**
 * Get the full EAP HPI banner status
 */
export const getEapHpiBannerStatus = createSelector(
  [getEapHpiBannerState, getIsEapHpiHomebaseBannerVisible, getIsEapHpiAccountBannerVisible],
  (bannerData, isHomebaseBannerVisible, isAccountBannerVisible) => {
    return fromJS({
      ...bannerData.toJS(),
      isHomebaseBannerVisible,
      isAccountBannerVisible,
    })
  },
)

/**
 * Get banner tracking properties for Mixpanel page view events
 */
export const getEapHpiBannerTrackingProperties = createSelector(
  [getEapHpiBannerNotificationType, getEapHpiBannerSessionsRemaining],
  (notificationType, sessionsRemaining) => {
    return {
      eap_to_hpi_add_health_plan_banner: notificationType === EapHpiNotificationType.ADD_HEALTH_PLAN,
      eap_to_hpi_add_payment_banner: notificationType === EapHpiNotificationType.ADD_PAYMENT_INFO,
      eap_to_hpi_view_cost_banner: notificationType === EapHpiNotificationType.VIEW_COST_ESTIMATE,
      eap_sessions_remaining: sessionsRemaining,
    }
  },
)

/**
 * Get banner type string for Mixpanel click/dismiss events
 */
export const getEapHpiBannerType = createSelector([getEapHpiBannerNotificationType], (notificationType): string => {
  switch (notificationType) {
    case EapHpiNotificationType.ADD_HEALTH_PLAN:
      return 'add_health_plan'
    case EapHpiNotificationType.ADD_PAYMENT_INFO:
      return 'add_payment_method'
    case EapHpiNotificationType.VIEW_COST_ESTIMATE:
      return 'view_cost_estimate'
    default:
      return ''
  }
})

/**
 * Get banner event properties for Mixpanel click/dismiss events
 */
export const getEapHpiBannerEventProperties = createSelector(
  [getEapHpiBannerSessionsRemaining, getEapHpiBannerType],
  (sessionsRemaining, bannerType) => {
    return {
      eap_sessions_left: sessionsRemaining,
      banner_type: bannerType,
    }
  },
)
